import React, { useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { FiSettings, FiFileText, FiPlus, FiBarChart, FiCalendar, FiMapPin, FiClock } from "react-icons/fi";
import { usePlanRestriction } from "../../context/PlanRestrictionContext";

// Import tab components (we'll create these next)
import OvertimeSettings from "./overtime/OvertimeSettings";
import OvertimeRequests from "./overtime/OvertimeRequests";
import OvertimeAssignment from "./overtime/OvertimeAssignment";
import OvertimeAnalytics from "./overtime/OvertimeAnalytics";
import OvertimeReports from "./overtime/OvertimeReports";
import LocationSettings from "./overtime/LocationSettings";

// Overtime Management Tabs Component
const OvertimeManagementTabs = () => {
  const [activeTab, setActiveTab] = useState(0);

  const overtimeTabConfig = [
    {
      name: "Settings",
      icon: <FiSettings size={16} />,
      component: <OvertimeSettings />,
      description: "Configure overtime policies and limits"
    },
    {
      name: "Requests",
      icon: <FiFileText size={16} />,
      component: <OvertimeRequests />,
      description: "Manage employee overtime requests"
    },
    {
      name: "Assignment",
      icon: <FiPlus size={16} />,
      component: <OvertimeAssignment />,
      description: "Manually assign overtime to employees"
    },
    {
      name: "Analytics",
      icon: <FiBarChart size={16} />,
      component: <OvertimeAnalytics />,
      description: "Overtime insights and trends"
    },
    {
      name: "Reports",
      icon: <FiCalendar size={16} />,
      component: <OvertimeReports />,
      description: "Weekly and monthly overtime reports"
    }
  ];

  return (
    <Box>
      <Tabs 
        index={activeTab} 
        onChange={setActiveTab}
        variant="enclosed"
        colorScheme="blue"
      >
        <TabList bg="gray.50">
          {overtimeTabConfig.map((tab, index) => (
            <Tab
              key={index}
              _selected={{ bg: "white", borderBottomColor: "white" }}
              _hover={{ bg: "gray.100" }}
              py={4}
              px={6}
            >
              <Box display="flex" alignItems="center" gap={2}>
                {tab.icon}
                <Text fontWeight="500">{tab.name}</Text>
              </Box>
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {overtimeTabConfig.map((tab, index) => (
            <TabPanel key={index} p={6}>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Heading size="md" color="gray.800" mb={2}>
                    {tab.name}
                  </Heading>
                  <Text color="gray.600" fontSize="sm">
                    {tab.description}
                  </Text>
                </Box>
                {tab.component}
              </VStack>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

const OvertimeManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { canAccessFeature } = usePlanRestriction();
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Feature access is now only restricted by user role (branch vs main branch)
  // All Main Branch users have access to Overtime Management regardless of plan

  const mainTabConfig = [
    {
      name: "Overtime Management",
      icon: <FiClock size={16} />,
      component: <OvertimeManagementTabs />,
      description: "Manage overtime policies, requests, and analytics"
    },
    {
      name: "Location Settings",
      icon: <FiMapPin size={16} />,
      component: <LocationSettings />,
      description: "Manage location radius and tracking settings"
    }
  ];

  return (
    <Box p={6} bg={bgColor} minH="100vh">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" color="gray.900" mb={2}>
            Overtime & Location Management
          </Heading>
          <Text color="gray.600" fontSize="md">
            Manage overtime policies, requests, analytics, and location tracking settings
          </Text>
        </Box>

        {/* Main Tabs */}
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
          overflow="hidden"
        >
          <Tabs 
            index={activeTab} 
            onChange={setActiveTab}
            variant="enclosed"
            colorScheme="blue"
          >
            <TabList bg="gray.50">
              {mainTabConfig.map((tab, index) => (
                <Tab
                  key={index}
                  _selected={{ bg: "white", borderBottomColor: "white" }}
                  _hover={{ bg: "gray.100" }}
                  py={4}
                  px={6}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    {tab.icon}
                    <Text fontWeight="500">{tab.name}</Text>
                  </Box>
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {mainTabConfig.map((tab, index) => (
                <TabPanel key={index} p={6}>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Heading size="md" color="gray.800" mb={2}>
                        {tab.name}
                      </Heading>
                      <Text color="gray.600" fontSize="sm">
                        {tab.description}
                      </Text>
                    </Box>
                    {tab.component}
                  </VStack>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
    </Box>
  );
};

export default OvertimeManagement; 